import { ClipboardList, FileText, Users, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import type { FamilyViewModel } from '@/models/family';

import { DocumentsSection } from './components/DocumentsSection';
import { MembersSection } from './components/MembersSection';
import { PhysicalChartCard } from './components/PhysicalChartCard';

/**
 * Single source of truth for the family-record sections. Same registry
 * shape as `PATIENT_RECORD_SECTIONS` so the sidebar, scroll-spy, and
 * default-section logic derive from one entry per section.
 *
 * PTV2-1157: the standalone "Overview" section was removed — the family
 * name + member count moved to the Info side-rail widget, and the Physical
 * chart is now its own section under Files (above Documents). Members is the
 * first entry, so it is the default landing section.
 */

export type FamilySectionGroup = 'people' | 'files';

export interface FamilySectionDescriptor {
  readonly id: string;
  readonly group: FamilySectionGroup;
  readonly labelKey: string;
  readonly Icon: LucideIcon;
  readonly render: (family: FamilyViewModel) => ReactNode;
}

export const FAMILY_RECORD_SECTIONS: readonly FamilySectionDescriptor[] = [
  {
    id: 'members',
    group: 'people',
    labelKey: 'routes.family.sidebar.members',
    Icon: Users,
    render: (family) => <MembersSection family={family} />,
  },
  {
    id: 'physical-chart',
    group: 'files',
    labelKey: 'routes.family.sidebar.physicalChart',
    Icon: ClipboardList,
    render: (family) => <PhysicalChartCard family={family} />,
  },
  {
    id: 'documents',
    group: 'files',
    labelKey: 'routes.family.sidebar.documents',
    Icon: FileText,
    render: (family) => <DocumentsSection guid={family.guid} />,
  },
];

export const FAMILY_SECTION_GROUP_ORDER: readonly FamilySectionGroup[] = [
  'people',
  'files',
];
